import React, { useEffect } from "react";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { Button } from "@emisgroup/ui-button";
import { useExecutionCodeList } from "../../../../../service/hooks";
import { ICode } from "../../redux/Types";
import { MultipleLine, Tooltip } from "../../../../../common/Tooltip";
import { Table, TableProps } from "../../../../Common/UI/Index";
import { ICodeListItem } from "../interfaces";
import useCsvExport from "../../../../../service/hooks/useCsvExport";
import { InfoIcon } from "./InfoIcon";
import { ExportIcon } from "./ExportIcon";
import { mapCodeListToTableRows, tableHeader, translateCodeListKeys } from "./utils/code-list-utils";

interface CodeListProps {
  isExportDisabled: boolean;
  selectedCode: ICode;
  onDataReady: (data: ICodeListItem[], hasErrors: boolean) => void;
}

export const CodeList: React.FC<CodeListProps> = ({ isExportDisabled, selectedCode, onDataReady }) => {
  const { t: translate } = useTranslation();
  const { executionId, ruleName, codeListId, artefactId } = selectedCode;
  const { data: { data: codeList }, error }= useExecutionCodeList(executionId, ruleName, codeListId, artefactId);
  const tableData: TableProps = {
    headerRow: tableHeader.map(item => ({ ...item, title: translate(item.title) })),
    dataRow: mapCodeListToTableRows(codeList)
  };
  const { exportToCSV } = useCsvExport();
  const totalPatients = codeList.reduce((acc, item) => {
    const countIncluded = typeof item.countIncluded === "number" ? item.countIncluded : 0;
    return acc + countIncluded;
  }, 0);

  const toolTipMessage: MultipleLine = [
    { text: translate("ResultDetails.CodeList.ToolTipMessageRow1") },
    { text: translate("ResultDetails.CodeList.ToolTipMessageRow2") }
  ];

  useEffect(() => { onDataReady(codeList, !!error); },[]);

  const exportHandler = (): void => {
    const headers = translateCodeListKeys(codeList[0], translate);
    exportToCSV(codeList, headers, `${codeListId}.csv`);
  };

  return (
    <>
      <div className="rules-details-actions">
        <div className="rules-details-count" data-testid="countInfo">
          { <b>{`${totalPatients}`}</b> } {`${translate("ResultDetails.CodeList.Patients")}`}
        </div>
        <div className="rules-details-export" data-testid="detailsExport">
          <Tooltip
            title={translate("ResultDetails.CodeList.DownloadAvailability")}
            text={ toolTipMessage }
            Trigger={InfoIcon}
          />
          <Button data-testid="export" disabled= {isExportDisabled} variant="filled" onClick={ exportHandler }>
            <ExportIcon/>
            { translate("ResultDetails.CodeList.Export") }
          </Button>
        </div>
      </div>
      <div className="rules-details-table">
        <Table
          id="codesList"
          tableData={tableData}
          emptyStateText={translate("ResultDetails.CodeList.NoEntriesFound")}/>
      </div>
    </>
  );
};
